<template>
  <ProgressSpinner v-if="!rxSWIN"/>
  <Container class="pt-6 w-full h-screen flex flex-col" v-if="rxSWIN">
    <div class="bg-surface-0 dark:bg-surface-900 -mx-8 px-8 -mt-6 pt-6 pb-4">

      <Breadcrumb :home="home" :model="items" class="mb-3">
        <template #item="{ item, props }">
            <a v-bind="props.action" @click="back">
              <i :class="[item.icon, 'text-color']"/>
              <span class="text-surface-500 dark:text-surface-400 font-semibold">{{ item.label }}</span>
            </a>
        </template>
      </Breadcrumb>

      <PageHeading>{{ rxSWIN.regulation?.name }}_{{ rxSWIN.identifier }}</PageHeading>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div class="grid grid-cols-6 lg:grid-cols-12 gap-4 max-w-7xl mx-auto py-6">
        <!-- RxSwin Summary Section -->
        <div class="col-span-full mt-2">
          <h4 data-cy="rxswin-summary-headline-name" class="text-lg font-bold text-surface-600 antialiased">{{ $t('pages.rx_swin_search.summary.rx_swin_summary') }}</h4>
        </div>
        <Card class="col-span-5 sm:grid-cols-4">
          <template #content>
            <DescriptionList data-cy="info-list" :title="$t('common.information')" class="-mt-6">
              <DescriptionListItem :term="$t('pages.rx_swin_search.summary.identifier')" :details="rxSWIN.identifier"/>
              <DescriptionListItem :term="$t('pages.rx_swin_search.summary.regulation')" :details="rxSWIN.regulation?.name"/>
              <DescriptionListItem :term="$t('pages.rx_swin_search.summary.vehicle_manufacturer')" :details="rxSWIN.vehicle_manufacturer?.name"/>
              <DescriptionListItem :term="$t('pages.rx_swin_search.summary.assets')" :details="assets?.total ?? 0"/>
              <DescriptionListItem :term="$t('pages.rx_swin_search.summary.hardware_included_in_configuration')" :details="rxSWIN.mappings?.length ?? 0"/>
            </DescriptionList>
          </template>
        </Card>

        <!-- Hardware / Asset Section -->
        <div class="col-span-full mt-8 grid grid-cols-8 lg:grid-cols-11">

          <!-- Hardware Section -->
          <div class="col-span-8 lg:col-span-5 pb-3 grid grid-cols-6 place-content-start">
            <h4 class="col-span-full text-lg font-bold text-surface-600 antialiased">{{ $t('pages.rx_swin_search.summary.hardware_configuration') }}</h4>
            <div class="text-xs text-surface-500 col-span-full mb-4">
              {{ $t('pages.rx_swin_search.summary.hardware_configuration_info') }}
            </div>
            <HardwareConfigurationCard :config="config" v-for="config in rxSWIN.mappings" class="col-span-3 sm:col-span-2 md:col-span-2 lg:col-span-3 xl:col-span-2 m-1"  v-if="rxSWIN.mappings.length > 0"/>
            <Card v-else class="col-span-6">
              <template #content>
                <div class="text-center flex items-center justify-center mb-7">
                  <IconSettingsExclamation class="text-primary-900 opacity-60" :size="40"/>
                  <span class="text-sm font-medium text-surface-500 pl-3">{{ $t('pages.rx_swin_search.summary.no_hardware_configurations') }}</span>
                </div>
              </template>
            </Card>
          </div>

          <Divider layout="vertical" class="col-span-1 hidden lg:block" />

          <!-- Asset Section -->
          <div class="col-span-8 lg:col-span-5  pb-3">
            <h4 class="col-span-full text-lg font-bold text-surface-600 antialiased">{{ $t('pages.rx_swin_search.summary.assets') }}</h4>
            <div class="text-xs text-surface-500 col-span-full mb-4">
              {{ $t('pages.rx_swin_search.summary.asset_info') }}
            </div>
            <ProgressSpinner class="col-span-full flex-1 flex flex-row" v-if="assetsLoading && assets?.data == undefined"/>
            <div v-else>
              <div v-if="assets?.data.length > 0" class="p-2 bg-white rounded-md border border-surface-100 shadow-md h-[25rem]">
                <DataTable
                data-cy="rxswin-assets-table"
                    :value="assets?.data"
                    data-key="id"
                    :rows="assetParams.per_page"
                    :first="getFirstPage(assetParams)"
                    @page="onPage($event)"
                    :rowsPerPageOptions="pages"
                    :totalRecords="assets?.total"
                    paginator
                    lazy
                    always-show-paginator
                    @update:rows="onRowsUpdate(assetParams.per_page, assetParams)"
                    scrollable
                    scroll-height="flex"
                    size="small"
                    @filter="onFilter($event, assetParams)"
                    filterDisplay="menu"
                    :loading="assetsLoading"
                >
                  <template #header>
                    <div class="flex flex-row">
                      <InputText
                          data-cy="search-asset-rxswin-summary-input"
                          :placeholder="$t('pages.assets.index.search_assets')"
                          v-model="assetParams.filter.search"
                          @keyup="onSearch(assetParams)"
                      />
                      <div class="inline-flex flex-1 grow flex-col items-end px-4 text-xs">
                        <small class="text-slate-400">Assets Total</small>
                        <span class="font-bold text-xs">{{ assets?.total?.toLocaleString() || '-' }}</span>
                      </div>
                    </div>
                  </template>
                  <Column field="name" :header="$t('common.name')">
                    <template #body="item">
                      <router-link :to="{name: 'assets.show', params: { asset: item.data.id }}"
                        link
                        size="small"
                        aria-haspopup="true"
                      >
                      {{ item.data.name }}
                      </router-link>
                    </template>
                  </Column>
                  <Column field="company.name" :header="$t('common.company')"></Column>
                </DataTable>
              </div>
              <Card v-else class="col-span-6">
                <template #content>
                  <div class="text-center flex items-center justify-center mb-7">
                    <IconTruck class="text-primary-900 opacity-60" :size="40"/>
                    <span class="text-sm font-medium text-surface-500 pl-3">{{ $t('pages.rx_swin_search.summary.no_assets') }}</span>
                  </div>
                </template>
              </Card>
            </div>
          </div>

        </div>
      </div>
    </div>
  </Container>
</template>
<script>
import PageHeading from "@/Components/PageHeading.vue";
import {useRxSWINStore} from "@/stores/rxSWIN.js";
import TabLayout from "@/Layouts/TabLayout.vue";
import {mapStores} from "pinia";
import DescriptionList from "@/Components/DescriptionList.vue";
import DescriptionListItem from "@/Components/DescriptionListItem.vue";
import AssetCard from "@/Widgets/Cards/AssetCard.vue";
import HardwareConfigurationCard from "@/Widgets/Cards/HardwareConfigurationCard.vue";
import {IconTruck, IconSettingsExclamation, IconPlus, IconSearch, IconRefresh} from "@tabler/icons-vue";
import Container from "@/Components/Container.vue";
import {useAssetsStore} from "@/stores/assets.js";
import {getFirstPage, onFilter, onPage, onRowsUpdate, onSearch} from "@/stores/_helpers.js";
import IndexHeaderStat from "@/Widgets/Cards/IndexHeaderStat.vue";
import _ from "lodash";

export default {
  name: "RxSwinSearchOverview",
  components: {
    IndexHeaderStat, IconRefresh,
    IconSearch, IconPlus,
    IconTruck,
    IconSettingsExclamation,
    AssetCard, HardwareConfigurationCard, DescriptionListItem, DescriptionList, TabLayout, PageHeading, Container},
  data() {
    return {
      rxSWIN: undefined,
      assets : undefined,
      assetsLoading : false,
      params: {
        fields: {rx_swins: ['id', 'identifier', 'regulation_id', 'vehicle_manufacturer_id']},
        include: [
          'regulation',
          'vehicleManufacturer',
          'mappings',
          'mappings.deviceRevision',
          'mappings.deviceRevision.deviceModel',
          'mappings.deviceRevision.deviceType',
          'mappings.firmware',
        ].join(','),
      },
      assetParams: {
        fields: {assets: ['id', 'name', 'company_id']},
        per_page: 20,
        page: 1,
        include: [
          'company',
        ].join(','),
        filter: {
          search: undefined,
          rx_swin_id: undefined
        },
      },
      pages: [
        10,
        20,
        50,
        100,
      ],
      home: {
        icon: 'ti ti-arrow-back-up text-xl',
        label: this.$t('common.back'),
      },
      items: []
    }
  },
  watch: {
    '$route.params.rxswin': {
      async handler(newValue, oldValue) {
        if (newValue) {
          this.rxSWIN = await this.fetchRxSWIN();
          this.fetchRxSWINAssets();
        } else {
          this.rxSWIN = undefined;
        }
      },
      immediate: true,
    }
  },
  methods: {
    onFilter,
    onRowsUpdate,
    getFirstPage,
    fetchRxSWIN() {
      return this.rxSWINStore.showRxSWIN(this.$route.params.rxswin, this.params);
    },
    async fetchRxSWINAssets() {
      this.assetsLoading = true
      this.assetParams.filter['rx_swin_id'] = this.$route.params.rxswin;
      this.assets = await this.assetsStore.indexAssets(this.assetParams);
      this.assetsLoading = false
    },
    back() { // work around for having multiple back buttons - clicking summary > asset summary was looping when clicking back
     return this.$router.push({ name: 'rxswin.search' });
    },
    onPage($event) {
      onPage($event, this.assetParams);
      this.fetchRxSWINAssets();
    },
    onSearch: _.debounce(function () {
          onSearch(this.assetParams)
          this.fetchRxSWINAssets();
        },
        500
    ),
  },
  getters: {

  },
  actions : {
  },
  computed: {
    ...mapStores(useRxSWINStore),
    ...mapStores(useAssetsStore),
  },
  mounted() {

  }
}
</script>

<style scoped>

</style>